import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import BackgroundMedia from "@/components/BackgroundMedia";
import Seo, { composeTitle, truncateForMeta } from "@/components/Seo";
import { Container, SectionLabel, ButtonLux, Reveal } from "@/components/primitives";
import { useSettings } from "@/context/SettingsContext";
import { api, mediaSrc } from "@/lib/api";

const FALLBACK_CATEGORIES = [
  { slug: "housewarming", index: "01", title: "Housewarming Gifts", tagline: "For spaces beginning a new chapter.", cta_label: "Explore", visible: true },
  { slug: "art-accessories", index: "02", title: "Art & Accessories", tagline: "For those who appreciate the unusual.", cta_label: "Explore", visible: true },
  { slug: "festive", index: "03", title: "Festive Gifting", tagline: "For occasions worth making memorable.", cta_label: "Explore", visible: true },
  { slug: "curated", index: "04", title: "Curated Gift Collection", tagline: "Tell us who. Tell us why. We'll curate the rest.", cta_label: "Start a Curated Gift", visible: true },
];

export default function Gifting() {
  const { settings } = useSettings();
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.getPage("gifting").then(setDoc).catch(() => {}); }, []);

  const c = doc?.content || {};
  const hero = c.hero || {};
  const intro = c.intro || {};
  const yu = c.yu_campaign || {};
  const raw = Array.isArray(c.categories?.items) && c.categories.items.length > 0 ? c.categories.items : FALLBACK_CATEGORIES;
  const categories = raw.filter((cat) => cat && cat.visible !== false && cat.title);
  const heroImage = hero.image_url ? mediaSrc(hero.image_url) : "";
  const ml = (t) => (t || "").split("\n").map((line, i) => <span key={i} className="block">{line}</span>);

  return (
    <div className="min-h-screen bg-lux-bg">
      <Seo
        title={composeTitle(hero.headline ? String(hero.headline).split("\n").join(" ") : "Gifting", settings.seo.default_title, settings.seo.title_suffix)}
        description={truncateForMeta(hero.sub || intro.body || "Gifts chosen with thought. Objects chosen to belong.")}
        path="/gifting"
        imageUrl={heroImage || undefined}
      />
      <TopBar />
      <div className="relative min-h-[520px] md:min-h-[640px] flex items-end overflow-hidden">
        {heroImage ? <BackgroundMedia imageUrl={heroImage} /> : <div className="absolute inset-0 bg-[#1F1B15]" />}
        <div className="absolute inset-0 bg-lux-ink/45 pointer-events-none" />
        <Container className="relative pt-40 pb-16 md:pb-20">
          <p className="text-[11px] tracking-[0.28em] uppercase text-white/70 mb-6">{hero.eyebrow || "· THE ART OF GIVING ·"}</p>
          <h1 className="font-serif text-white font-light tracking-tight leading-tight text-[clamp(2.4rem,6vw,4.5rem)]">{ml(hero.headline || "Gifts chosen with thought.\nObjects chosen to belong.")}</h1>
          {hero.sub ? <p className="text-white/85 font-light leading-relaxed mt-5 text-base md:text-lg max-w-[620px]">{hero.sub}</p> : null}
        </Container>
      </div>

      {(intro.eyebrow || intro.body) && (
        <Container className="py-20 md:py-24">
          {intro.eyebrow ? <SectionLabel>{intro.eyebrow}</SectionLabel> : null}
          <p className="font-serif text-lux-ink font-light tracking-tight leading-snug mt-4 text-xl md:text-3xl max-w-[780px]">
            {intro.body || "Onedot6 helps you find the right gift by starting with the reason — not the object."}
          </p>
        </Container>
      )}

      <Container className="pb-20 grid sm:grid-cols-2 gap-x-6 gap-y-8">
        {categories.map((cat, i) => {
          const href = cat.slug === "curated" ? "/gifting/curated" : `/gifting/${cat.slug}`;
          const img = cat.image_url ? mediaSrc(cat.image_url) : "";
          return (
            <Reveal key={cat.slug || i} delay={(i % 2) * 100}>
              <Link to={href} data-testid={`gifting-cat-${cat.slug}`} className="group block bg-lux-surface">
                <div className="relative overflow-hidden h-80">
                  {img ? <img src={img} alt={cat.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="w-full h-full bg-[#EAE3D8]" />}
                  <span className="absolute top-4 left-4 px-2.5 py-1.5 bg-white/90 text-[10px] tracking-[0.25em] text-lux-ink">{cat.index || ""}</span>
                </div>
                <div className="p-6 flex flex-col gap-2">
                  <h3 className="font-serif text-2xl text-lux-ink">{cat.title}</h3>
                  <p className="text-sm text-lux-sec leading-relaxed font-light">{cat.tagline}</p>
                  <div className="flex items-center gap-2.5 mt-3">
                    <span className="text-[11px] tracking-[0.25em] text-lux-accent">{(cat.cta_label || "Explore").toUpperCase()}</span>
                    <span className="text-sm text-lux-accent">→</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </Container>

      {yu.enabled !== false && (yu.title || yu.tagline) && (
        <Container className="pb-24">
          <div className="bg-[#F5EFE3] flex flex-col md:flex-row md:items-center overflow-hidden">
            {yu.image_url ? <img src={mediaSrc(yu.image_url)} alt={yu.title} className="w-full md:w-[48%] h-56 md:h-[340px] object-cover" /> : null}
            <div className="flex-1 p-6 md:p-10 flex flex-col gap-3">
              {yu.eyebrow ? <SectionLabel>{yu.eyebrow}</SectionLabel> : null}
              <h2 className="font-serif text-2xl md:text-4xl text-lux-ink font-light tracking-tight">{yu.title || "The Art of Giving by YÜ"}</h2>
              {yu.tagline ? <p className="text-[15px] text-lux-sec font-light leading-relaxed">{yu.tagline}</p> : null}
              {yu.cta_label ? <div className="mt-2"><ButtonLux label={yu.cta_label} variant="outline" to={yu.cta_href || "/gifting/art-accessories"} testId="yu-cta" /></div> : null}
            </div>
          </div>
        </Container>
      )}

      <Container className="py-20 md:py-24 border-t border-lux-line">
        <SectionLabel>· Can’t Decide? ·</SectionLabel>
        <h2 className="font-serif text-2xl md:text-3xl text-lux-ink font-light tracking-tight mt-3 mb-3">Let the studio curate for you.</h2>
        <p className="text-[15px] text-lux-sec font-light leading-relaxed max-w-[620px]">
          Share the recipient, the occasion and any preferences. We’ll shape a considered selection and share it back within one working day.
        </p>
        <div className="mt-5"><ButtonLux label="Start a Curated Gift" to="/gifting/curated" testId="closing-curated-cta" /></div>
      </Container>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
